import {noop} from '@benchling/js-utils';
import {createStyles, Flex, Text} from '@mantine/core';
import React, {useMemo, useRef} from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FilledScratchPlateData} from 'notebook/integrations/plate-maps/scratch-plates/FilledScratchPlateUtil';
import {
  scratchPlateSchemaId,
  useWellSchemaOptionsForPlateMap,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import {SCRATCH_PLATE_VIEWER_MIN_HEIGHT} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewConstants';
import useFilledScratchPlateData, {
  UseFilledScratchPlateDataResult,
} from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import ViewOnlyPlateMapPane from 'notebook/integrations/plate-maps/scratch-plates/ViewOnlyPlateMapPane';
import PlateMapDisplayLayerDropdown from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerDropdown';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  ReagentType,
  SupportedAdditionalAttribute,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getContentConcentrationOptions,
  getDisplayLayerAttributeKey,
  getMetadataLayerDisplayOptions,
  getParameterLayerDisplayOptions,
  getReagentFinalConcentrationOptions,
  getReagentTransferQuantityOptions,
} from 'samples/plate-maps/PlateMapUtil';
import {openPutScratchPlateInInventoryModal} from 'samples/plate-maps/scratch-plates/PutScratchPlateInInventoryModal';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal, {RunModalProps} from 'ui-library/runModal';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

const BannerWrapper: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {classes} = useStyles();
  return <div className={classes.bannerWrapper}>{children}</div>;
};

export const LOADING_PLATE_CONTENTS_TEXT = 'Loading plate contents…';

// Plain loading text above the spinner, rather than an info banner.
const PlateLoadingState: React.FC = () => {
  const {classes} = useStyles();
  return (
    <Flex direction="column" align="center" justify="center" gap="sm" className={classes.loadingState}>
      <Text>{LOADING_PLATE_CONTENTS_TEXT}</Text>
      <CenteredSpinner />
    </Flex>
  );
};

export enum QueryBannerState {
  LOADING = 'loading',
  ERROR = 'error',
  INACCESSIBLE_PLATE = 'inaccessible-plate',
}

export const QUERY_BANNER_CONTENT: Record<
  QueryBannerState.ERROR | QueryBannerState.INACCESSIBLE_PLATE,
  {
    bannerType: InlineBannerType;
    text: string;
    supportingInfo?: React.ReactNode;
  }
> = {
  [QueryBannerState.INACCESSIBLE_PLATE]: {
    bannerType: InlineBannerType.ATTENTION,
    text: 'You do not have access to this plate.',
  },
  [QueryBannerState.ERROR]: {
    bannerType: InlineBannerType.ERROR,
    text: 'Failed to load this plate.',
  },
};

export const TRUNCATED_BANNER_TEXT = "Some of this plate's contents aren't shown.";
export const TRUNCATED_BANNER_SUPPORTING_INFO = 'This plate has more data than can be loaded at once.';

interface ScratchPlateViewerProps {
  result: UseFilledScratchPlateDataResult;
}

const ScratchPlateViewerShell: React.FC<{
  result: UseFilledScratchPlateDataResult;
  renderSuccess: (data: FilledScratchPlateData) => JSX.Element;
}> = ({result, renderSuccess}) => {
  switch (result.kind) {
    case 'query': {
      const {query} = result;
      let state: QueryBannerState;
      if (query.type === QueryResultType.PENDING || query.type === QueryResultType.SKIPPED) {
        // SKIPPED occurs when query inputs are not ready yet, so this still presents as loading.
        state = QueryBannerState.LOADING;
      } else if (query.type === QueryResultType.ERROR) {
        state = QueryBannerState.ERROR;
      } else if (isInaccessible(query.data.scratchPlate)) {
        state = QueryBannerState.INACCESSIBLE_PLATE;
      } else {
        // Plate fetch succeeded but downstream data frames are still settling.
        state = QueryBannerState.LOADING;
      }
      if (state === QueryBannerState.LOADING) {
        return <PlateLoadingState />;
      }
      const banner = QUERY_BANNER_CONTENT[state];
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={banner.bannerType}
            text={banner.text}
            supportingInfo={banner.supportingInfo}
          />
        </BannerWrapper>
      );
    }
    case 'missing-content-layer':
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="No contents found."
            supportingInfo={<>This plate appears to be empty.</>}
          />
        </BannerWrapper>
      );
    case 'hydration-error':
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Failed to load the contents of this plate."
          />
        </BannerWrapper>
      );
    case 'unregistered-samples':
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="This plate contains samples that aren't linked to registered entities."
          />
        </BannerWrapper>
      );
    case 'success':
      return renderSuccess(result.data);
    default:
      return assertNever(result);
  }
};

export const ScratchPlateViewer: React.FC<ScratchPlateViewerProps> = ({result}) => {
  const {classes} = useStyles();
  return (
    <ScratchPlateViewerShell
      result={result}
      renderSuccess={(data) => (
        <>
          <FilledScratchPlateHeader plateName={null} isTruncated={data.isTruncated} />
          <FilledScratchPlateView data={data} plateMapPaneClassName={classes.plateMapPane} />
        </>
      )}
    />
  );
};

export const FilledScratchPlateHeader: React.FC<{
  plateName: string | null;
  isTruncated: boolean;
}> = ({plateName, isTruncated}) => {
  const {classes} = useStyles();
  return (
    <>
      {plateName != null && plateName !== '' && <div className={classes.plateName}>{plateName}</div>}
      {isTruncated && (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={TRUNCATED_BANNER_TEXT}
            supportingInfo={TRUNCATED_BANNER_SUPPORTING_INFO}
          />
        </BannerWrapper>
      )}
    </>
  );
};

const ADDITIONAL_ATTRIBUTE_OPTIONS: Array<SupportedAdditionalAttribute> = [
  REAGENTS_ATTRIBUTE_SENTINEL,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
];

export const FilledScratchPlateView: React.FC<{
  data: FilledScratchPlateData;
  maxPlateViewHeight?: string;
  plateMapPaneClassName?: string;
  reagentAssignmentGroupsOverride?: {[id: string]: ReagentAssignmentGroup};
  /** Rendered above the layer dropdown inside the plate-width header slot, so it aligns with the plate's left edge. */
  headerPrefix?: JSX.Element;
}> = ({
  data,
  maxPlateViewHeight = '500px',
  plateMapPaneClassName,
  reagentAssignmentGroupsOverride,
  headerPrefix,
}) => {
  const {plateMap, containerSchemaId} = data;
  const {classes} = useStyles();

  const wellSchemaLayers = useWellSchemaLayers(containerSchemaId, noop, []);
  const wellSchemaOptions = useWellSchemaOptionsForPlateMap(plateMap, wellSchemaLayers);

  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute, displayLayer} =
    usePlateMapDisplayAttributeState(plateMap, REAGENTS_ATTRIBUTE_SENTINEL, true);

  const metadataLayersOptions = useMemo(
    () => getMetadataLayerDisplayOptions(Object.values(plateMap.metadataLayerInfoById ?? {})),
    [plateMap.metadataLayerInfoById]
  );

  const plateDataParametersOptions = useMemo(
    () => getParameterLayerDisplayOptions(Object.values(plateMap.parameterLayerInfoById ?? {})),
    [plateMap.parameterLayerInfoById]
  );

  // Build measurement layer options for both concentration (from contents) and transfer quantity
  // (from sources, present when the plate was built from resolved sources in ReviewStep).
  const measurementLayersOptions = useMemo(() => {
    const allReagentGroupsById =
      reagentAssignmentGroupsOverride ??
      (assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(REAGENTS_ATTRIBUTE_SENTINEL)] as {
        [id: string]: ReagentAssignmentGroup;
      }) ??
      {};

    const contentGroupIds = new Set<string>();
    const sourceConcentrationGroupIds = new Set<string>();
    const transferQuantityGroupIds = new Set<string>();
    for (const well of plateMap.wells) {
      for (const content of well.contents) {
        contentGroupIds.add(content.groupId);
      }
      for (const source of well.sources) {
        if (source.type !== ReagentType.SOURCE_CONTAINABLE) {
          continue;
        }
        if (source.finalConcentration.measurement.value != null) {
          sourceConcentrationGroupIds.add(source.groupId);
        }
        if (source.transferQuantity.measurement.value != null) {
          transferQuantityGroupIds.add(source.groupId);
        }
      }
    }

    const concentrationOptions = [
      ...getContentConcentrationOptions(
        Object.fromEntries(Object.entries(allReagentGroupsById).filter(([id]) => contentGroupIds.has(id)))
      ),
      ...getReagentFinalConcentrationOptions(
        Object.fromEntries(
          Object.entries(allReagentGroupsById).filter(([id]) => sourceConcentrationGroupIds.has(id))
        )
      ),
    ];
    const transferQuantityOptions = getReagentTransferQuantityOptions(
      Object.fromEntries(
        Object.entries(allReagentGroupsById).filter(([id]) => transferQuantityGroupIds.has(id))
      )
    );

    return [...concentrationOptions, ...transferQuantityOptions];
  }, [assignmentGroupsPerAttribute, reagentAssignmentGroupsOverride, plateMap]);

  const assignmentGroupsById =
    reagentAssignmentGroupsOverride != null && selectedAttribute === REAGENTS_ATTRIBUTE_SENTINEL
      ? reagentAssignmentGroupsOverride
      : (assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {});

  return (
    <ViewOnlyPlateMapPane
      header={
        <>
          {headerPrefix}
          <div className={classes.layerDropdown}>
            <PlateMapDisplayLayerDropdown
              additionalAttributeOptions={ADDITIONAL_ATTRIBUTE_OPTIONS}
              selectedAttribute={selectedAttribute}
              setSelectedAttribute={setSelectedAttribute}
              plateDataParametersOptions={plateDataParametersOptions}
              wellSchemaOptions={wellSchemaOptions}
              measurementLayersOptions={measurementLayersOptions}
              metadataLayersOptions={metadataLayersOptions}
            />
          </div>
        </>
      }
      plateMapDisplayParams={{
        plateMap,
        displayLayer,
        attribute: selectedAttribute,
        assignmentGroupsById,
        groupedValidationErrors: {},
      }}
      maxPlateViewHeight={maxPlateViewHeight}
      className={plateMapPaneClassName}
    />
  );
};

// Separate component so usePlateMapDisplayAttributeState runs only once data.plateMap exists (rules of hooks).
const ContentsLayerScratchPlateView: React.FC<{data: FilledScratchPlateData}> = ({data}) => {
  const {plateMap} = data;

  const {assignmentGroupsPerAttribute, displayLayer} = usePlateMapDisplayAttributeState(
    plateMap,
    REAGENTS_ATTRIBUTE_SENTINEL,
    true
  );

  const assignmentGroupsById =
    assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(REAGENTS_ATTRIBUTE_SENTINEL)] ?? {};

  return (
    <ViewOnlyPlateMapPane
      plateMapDisplayParams={{
        plateMap,
        displayLayer,
        attribute: REAGENTS_ATTRIBUTE_SENTINEL,
        assignmentGroupsById,
        groupedValidationErrors: {},
      }}
    />
  );
};

export const ContentsLayerScratchPlateViewer: React.FC<ScratchPlateViewerProps> = ({result}) => (
  <ScratchPlateViewerShell
    result={result}
    renderSuccess={(data) => <ContentsLayerScratchPlateView data={data} />}
  />
);

const useStyles = createStyles((theme) => ({
  plateMapPane: {
    height: '100%',
  },
  bannerWrapper: {
    marginTop: theme.spacing.lg,
  },
  loadingState: {
    flex: 1,
    height: '100%',
    // Keeps the message centered when the parent has no explicit height.
    minHeight: SCRATCH_PLATE_VIEWER_MIN_HEIGHT,
  },
  plateName: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    fontSize: theme.fontSizes.xl,
    marginBottom: theme.spacing.md,
  },
  layerDropdown: {
    // Sized to fit the longest localized label on a single line; not a theme spacing token.
    maxWidth: 264,
  },
  chipModalHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing.sm,
  },
}));

interface ScratchPlateChipModalProps extends RunModalProps<void> {
  scratchPlateId: string;
}

function ScratchPlateChipModal({scratchPlateId, close}: ScratchPlateChipModalProps): JSX.Element {
  const {classes} = useStyles();
  const {result} = useFilledScratchPlateData(scratchPlateId);

  const plateInfoRef = useRef<{
    name: string | null;
    height: number;
    width: number;
    plateSchemaId: string | null;
  } | null>(null);

  if (
    result.kind === 'query' &&
    result.query.type === QueryResultType.SUCCESS &&
    result.query.data.scratchPlate.__typename === 'ScratchPlateNode'
  ) {
    const sp = result.query.data.scratchPlate;
    plateInfoRef.current = {
      name: sp.name,
      height: sp.height,
      width: sp.width,
      plateSchemaId: scratchPlateSchemaId(sp),
    };
  }

  const plateInfo = plateInfoRef.current;

  function handlePutInInventory(): void {
    if (plateInfo == null) {
      return;
    }
    close();
    openPutScratchPlateInInventoryModal({
      showSelectPlatesStep: false,
      plates: [
        {
          scratchPlateId,
          scratchPlateName: plateInfo.name,
          plateSize: [plateInfo.height, plateInfo.width],
          plateSchemaId: plateInfo.plateSchemaId,
          // This button only renders when the plate has no inventory plate yet.
          inventoryPlateId: null,
          // GetScratchPlateDesignSummary doesn't fetch well states or createdAt.
          filledPositions: null,
          createdAt: null,
        },
      ],
    });
  }

  return (
    <>
      <div className={classes.chipModalHeader}>
        <Button
          size={ButtonSize.SMALL}
          icon="box-arrow-right"
          text="Put in inventory"
          buttonStyle={ButtonStyle.SECONDARY}
          disabled={plateInfo == null}
          onClick={handlePutInInventory}
        />
      </div>
      <ScratchPlateViewer result={result} />
    </>
  );
}

export function openScratchPlateChipModal({
  scratchPlateId,
  plateName,
}: {
  scratchPlateId: string;
  plateName: string;
}): void {
  runModal(
    ScratchPlateChipModal,
    {scratchPlateId},
    {
      title: `Plate: ${plateName}`,
      size: 'xl',
      // Sit below the layer dropdown's portal (z-index 1051) so it stacks above this modal.
      zIndex: 1040,
    }
  );
}

export default ScratchPlateViewer;
